import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { canManageOrganizations } from '@/shared/session/roles';
import { session } from '@/shared/session/session';

import { StonesPanel } from '../components/stones-panel';
import { SuppliersPanel } from '../components/suppliers-panel';
import type { StoneFilters } from '../schemas/diamonds-search';

const ROUTE_ID = '/_authed/diamonds' as const;

function Diamonds() {
  const { t } = useTranslation('diamonds');
  const { me } = session.useSession();
  const canManage = canManageOrganizations(me?.user.role);
  const { tab = 'stones', ...filters } = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/diamonds' });

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={t('description')}
      />
      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              tab: next === 'stones' ? undefined : (next as 'suppliers'),
            }),
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="stones">{t('tabs.stones')}</TabsTrigger>
          <TabsTrigger value="suppliers" data-tour="platform.diamonds.suppliers-tab">
            {t('tabs.suppliers')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="stones" className="pt-4">
          <StonesPanel
            filters={filters}
            canManage={canManage}
            onFiltersChange={(next: StoneFilters) =>
              void navigate({ search: { ...next }, replace: true })
            }
          />
        </TabsContent>
        <TabsContent value="suppliers" className="pt-4">
          <SuppliersPanel canManage={canManage} />
        </TabsContent>
      </Tabs>
    </PageContent>
  );
}

export function DiamondsPage() {
  return (
    <FeatureErrorBoundary name="diamonds">
      <Diamonds />
    </FeatureErrorBoundary>
  );
}
